import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { transactionsAPI } from '../services/api';
import PageShell from '../components/PageShell';
import { ArrowUpRight, ArrowDownLeft, ClockCounterClockwise, Receipt } from '@phosphor-icons/react';

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'sent',     label: 'Sent' },
  { key: 'received', label: 'Received' },
  { key: 'topup',    label: 'Top-ups' },
  { key: 'purchase', label: 'Purchases' },
];

export default function TransactionHistoryPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    transactionsAPI.getHistory(100).then((res) => setTransactions(res.data)).catch((err) => console.error('Failed to load transactions:', err)).finally(() => setLoading(false));
  }, []);

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const filtered = transactions.filter((t) => {
    if (t.type === 'platform_fee') return false;
    if (filter === 'all') return true;
    if (filter === 'sent') return t.from_user_id === user?.user_id;
    if (filter === 'received') return t.to_user_id === user?.user_id;
    if (filter === 'topup') return t.type === 'topup';
    if (filter === 'purchase') return t.type === 'purchase';
    return true;
  });

  const label = (t) => {
    if (t.type === 'topup') return 'Wallet Top-up';
    if (t.type === 'purchase') return 'Purchase';
    if (t.to_user_id === user?.user_id) return `From ${t.from_email}`;
    return `To ${t.to_email}`;
  };

  return (
    <PageShell testId="history-page" back title={{ line1: 'History', line2: 'All your transactions' }}>
      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-2 mb-5 no-scrollbar">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-4 py-2 rounded-full border-[3px] text-sm font-bold tracking-tight whitespace-nowrap transition-colors ${
              filter === f.key
                ? 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/10'
                : 'border-white text-white active:bg-white/10'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-white/50">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-black border-[3px] border-white rounded-[18px] py-12 text-center">
          <ClockCounterClockwise className="w-14 h-14 mx-auto mb-3 text-white/30" weight="fill" />
          <p className="text-white/60 text-sm">No transactions found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((txn, i) => {
            const incoming = txn.type === 'topup' || txn.to_user_id === user?.user_id;
            return (
              <div
                key={txn.transaction_id}
                className="bg-black border-[3px] border-white rounded-[18px] px-4 py-3 flex items-center gap-3"
                data-testid={`transaction-${i}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  incoming ? 'bg-[#22C55E]/15 text-[#22C55E]' : 'bg-red-500/15 text-red-400'
                }`}>
                  {incoming ? <ArrowDownLeft className="w-6 h-6" weight="bold" /> : <ArrowUpRight className="w-6 h-6" weight="bold" />}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate">{label(txn)}</p>
                  <p className="text-xs text-white/40">{formatDate(txn.created_at)}</p>
                  {txn.note && <p className="text-[11px] text-white/40 italic truncate mt-0.5">&ldquo;{txn.note}&rdquo;</p>}
                </div>

                <div className="text-right flex-shrink-0">
                  <p className={`text-lg font-extrabold ${incoming ? 'text-[#22C55E]' : 'text-white'}`} style={{ fontFamily: 'Cabinet Grotesk' }}>
                    {incoming ? '+' : '−'}{formatCurrency(txn.amount)}
                  </p>
                  {txn.fee > 0 && <p className="text-[10px] text-white/40" data-testid={`fee-label-${i}`}>Fee: {formatCurrency(txn.fee)}</p>}
                  <Link
                    to={`/receipt/${txn.transaction_id}`}
                    className="inline-flex items-center gap-1 text-[10px] text-[#22C55E] hover:underline mt-1"
                    data-testid={`receipt-link-${i}`}
                  >
                    <Receipt className="h-3 w-3" /> Receipt
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
